import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, User, Bell, Menu, X } from "lucide-react";

export default function PortalLayout({ role, brand, navItems }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        if (user === null) nav(`/login?role=${role}`);
        else if (user && user.role !== role) nav(`/login?role=${role}`);
    }, [user, role, nav]);

    if (user === undefined) return <div className="min-h-screen bg-[var(--ebg-ivory)] flex items-center justify-center text-black/40">Loading…</div>;
    if (!user || user.role !== role) return null;

    const doLogout = async () => { await logout(); nav("/"); };

    return (
        <div className="min-h-screen flex bg-[var(--ebg-ivory)]" data-testid={`portal-${role}`}>
            {/* Sidebar */}
            {menuOpen && <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMenuOpen(false)} data-testid="portal-menu-overlay"/>}
            <aside className={`w-64 shrink-0 bg-[#0a0d18] text-white flex flex-col h-screen fixed inset-y-0 left-0 z-50 transition-transform duration-300 lg:sticky lg:top-0 lg:translate-x-0 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`} data-testid="portal-sidebar">
                <div className="p-6 border-b border-white/10 relative">
                    <button onClick={() => setMenuOpen(false)} className="lg:hidden absolute top-5 right-4 text-white/60 hover:text-white" data-testid="portal-menu-close" aria-label="Close menu"><X size={18}/></button>
                    <Link to="/" className="text-[var(--ebg-gold)] font-serif text-2xl">EBG Realty</Link>
                    <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-white/50 mt-2">{brand}</div>
                </div>
                <nav className="flex-1 py-6 space-y-1 overflow-y-auto">
                    {navItems.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            onClick={() => setMenuOpen(false)}
                            data-testid={`nav-${n.to.split("/").pop()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 mx-3 px-4 py-3 text-[13px] transition-all ${isActive ? "bg-[var(--ebg-gold)]/12 text-[var(--ebg-gold)] border-l-2 border-[var(--ebg-gold)]" : "text-white/65 hover:text-white hover:bg-white/5 border-l-2 border-transparent"}`
                            }
                        >
                            <n.icon size={16}/>
                            <span>{n.l}</span>
                            {n.badge > 0 && <span className="ml-auto text-[10px] bg-[var(--ebg-gold)] text-black px-2 py-0.5 rounded-full font-bold">{n.badge}</span>}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[var(--ebg-gold)]/15 border border-[var(--ebg-gold)]/40 text-[var(--ebg-gold)] flex items-center justify-center">
                            <User size={16}/>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-bold truncate">{user.name}</div>
                            <div className="text-[11px] text-white/50 truncate">{user.email}</div>
                        </div>
                    </div>
                    <button data-testid="portal-logout" onClick={doLogout} className="mt-4 w-full flex items-center justify-center gap-2 border border-white/10 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold text-white/70 hover:text-white hover:border-white/30">
                        <LogOut size={13}/> Sign Out
                    </button>
                </div>
            </aside>
            {/* Content */}
            <main className="flex-1 min-w-0 overflow-x-hidden">
                {/* Top bar */}
                <header className="bg-white border-b border-black/8 px-4 md:px-8 py-4 md:py-5 flex items-center justify-between gap-3">
                    <button onClick={() => setMenuOpen(true)} className="lg:hidden w-10 h-10 border border-black/12 flex items-center justify-center shrink-0" data-testid="portal-menu-open" aria-label="Open menu"><Menu size={18}/></button>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--ebg-gold)]">{brand}</div>
                        <div className="font-serif text-xl md:text-2xl mt-0.5 truncate">Welcome back, {user.name.split(" ")[0]}</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button className="w-10 h-10 rounded-full border border-black/12 flex items-center justify-center hover:border-black transition-colors relative">
                            <Bell size={16}/>
                            <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500"/>
                        </button>
                    </div>
                </header>
                <div className="p-4 md:p-8">
                    <Outlet/>
                </div>
            </main>
        </div>
    );
}
